import { FC, useState } from "react";
import { ITutorAccrual, tutorService } from "../../../entities/tutor";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { getDateUTC } from "../../../shared/lib/helpers/getDateUTC";
import { formatUtcToMsk } from "../../../shared/lib/helpers/formatUtcToMsk";
import { Calendar } from "../../../shared/ui/calendar";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import classes from "./staffAccruals.module.scss";

interface IProps {
    userId: number;
}

export const StaffAccruals: FC<IProps> = ({userId}) => {
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const [items, setItems] = useState<ITutorAccrual[] | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)

    const load = async (from: string, to: string) => {
        try{
            setIsLoading(true)
            const response = await tutorService.getAccruals(userId, from, to)
            setItems(response)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: "error"})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: "error"})
            }
            else{
                setGlobalMessage({message: "Ошибка при получении начислений сотрудника", type: "error"})
            }
            setItems([])
        }
        finally{
            setIsLoading(false)
        }
    }

    const setDate = (startDate: Date | null, endDate: Date | null) => {
        setStartDate(startDate)
        setEndDate(endDate)
        if(startDate && endDate){
            load(getDateUTC(startDate), getDateUTC(endDate))
        }
        if(!startDate && !endDate){
            setItems(null)
        }
    }

    const getAccrualDate = (item: ITutorAccrual & {
        actual_at?: string;
        actualAt?: string;
        created_at?: string;
        createdAt?: string;
    }) => {
        return item.created_at || item.createdAt || item.date || item.actual_at || item.actualAt || ''
    }

    const isPaid = (item: ITutorAccrual) => item.is_paid ?? item.isPaid ?? item.is_receipt

    const getAccrualType = (item: ITutorAccrual) => item.acural_type || item.acuralType || ''

    const getAccrualTypeLabel = (item: ITutorAccrual) => {
        switch (getAccrualType(item)) {
            case 'BILLING_ACCRUAL_TYPE_LESSON':
                return 'Занятие'
            case 'BILLING_ACCRUAL_TYPE_PENALTY':
                return 'Депремирование'
            case 'BILLING_ACCRUAL_TYPE_BONUS':
                return 'Премирование'
            case 'BILLING_ACCRUAL_TYPE_SHIFT_EXTRA_TEAM':
                return 'Доплата за звено'
            case 'BILLING_ACCRUAL_TYPE_SHIFT':
                return 'Смена'
            default:
                return ''
        }
    }

    const getAccrualTypeClassName = (item: ITutorAccrual) => {
        switch (getAccrualType(item)) {
            case 'BILLING_ACCRUAL_TYPE_LESSON':
                return classes.typeLesson
            case 'BILLING_ACCRUAL_TYPE_PENALTY':
                return classes.typePenalty
            case 'BILLING_ACCRUAL_TYPE_BONUS':
                return classes.typeBonus
            case 'BILLING_ACCRUAL_TYPE_SHIFT_EXTRA_TEAM':
                return classes.typeExtraTeam
            case 'BILLING_ACCRUAL_TYPE_SHIFT':
                return classes.typeShift
            default:
                return classes.typeDefault
        }
    }

    return (
        <section className={classes.container}>
            <section className={classes.calendar}>
                <Calendar
                    onDateRangeSelect={setDate}
                    selectedStartDate={startDate}
                    selectedEndDate={endDate}
                />
            </section>
            {isLoading ? (
                <section className={classes.loader}>
                    <LoaderSpinner />
                </section>
            ) : items ? (
                items.length ? (
                    <section className={classes.list}>
                        {items.map(item => (
                            <article key={item.id} className={classes.card}>
                                <section className={classes.top}>
                                    <section className={classes.date}>
                                        {getAccrualDate(item) ? `${formatUtcToMsk(getAccrualDate(item))} (мск)` : 'Дата не указана'}
                                    </section>
                                    <section className={classes.amount}>{item.amount} ₽</section>
                                    <section className={`${classes.status} ${isPaid(item) ? classes.paid : classes.unpaid}`}>
                                        {isPaid(item) ? 'Оплачено' : 'Не оплачено'}
                                    </section>
                                    {(getAccrualType(item) || getAccrualTypeLabel(item) || item.comment) && (
                                        <section className={classes.meta}>
                                            {(getAccrualTypeLabel(item) || getAccrualType(item)) && (
                                                <section className={`${classes.typeBadge} ${getAccrualTypeClassName(item)}`}>
                                                    {getAccrualTypeLabel(item) || 'Тип не указан'}
                                                </section>
                                            )}
                                            {item.comment && (
                                                <section className={classes.comment}>
                                                    {item.comment}
                                                </section>
                                            )}
                                        </section>
                                    )}
                                </section>
                            </article>
                        ))}
                    </section>
                ) : (
                    <section className={classes.empty}>Начислений за период не найдено</section>
                )
            ) : null}
        </section>
    )
}
